import { StyleSheet } from 'react-native';
import type { TextStyle, ViewStyle } from 'react-native';
import { WORDLE_CHIP_COLORS, type WordleTileState } from '../../../src/quiz/constants/warmup.constants';

export const wordleTileStyles = StyleSheet.create({
  // Outer wrapper paints the 3px chip "ledge"; the face area sits on top and leaves it exposed.
  outer: {
    borderRadius: 11,
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  // The square face: a gradient fill layer with the letter rendered on top of it.
  faceArea: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  faceGradient: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: 11,
  },
  text: {
    fontWeight: '800',
    textTransform: 'uppercase',
    textAlign: 'center',
    includeFontPadding: false,
  },
});

// Per-state chip palette as named styles (selected by the finite WordleTileState), so the component
// never builds inline color objects. Single-sourced from WORDLE_CHIP_COLORS.
const TILE_STATES: WordleTileState[] = ['empty', 'filled', 'correct', 'present', 'absent'];

const buildTileStyles = <T extends ViewStyle | TextStyle>(
  pick: (state: WordleTileState) => T,
): Record<WordleTileState, T> =>
  StyleSheet.create(
    Object.fromEntries(TILE_STATES.map((state) => [state, pick(state)])) as Record<WordleTileState, T>,
  );

/** Outer ledge color per tile state. */
export const wordleTileLedgeStyles = buildTileStyles<ViewStyle>((state) => ({
  backgroundColor: WORDLE_CHIP_COLORS[state].ledge,
}));

/** Face border (only drawn for empty/filled) per tile state. */
export const wordleTileBorderStyles = buildTileStyles<ViewStyle>((state) => {
  const { border } = WORDLE_CHIP_COLORS[state];
  return { borderWidth: border ? 1.5 : 0, borderColor: border };
});

/** Letter color per tile state. */
export const wordleTileTextStyles = buildTileStyles<TextStyle>((state) => ({
  color: WORDLE_CHIP_COLORS[state].text,
}));
